import { useRef, useState, type DragEvent } from 'react';
import { alpha, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { toast } from 'sonner';

import { CommonFormControl } from './CommonFormControl';

type ProductPhotoFieldProps = {
  file: File | null;
  imageUrl: string;
  /** null = discard the newly chosen file (keep the saved photo). */
  onFileChange: (file: File | null) => void;
};

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Photo picker for Add/Edit Property: one click-or-drop zone that shows the
 * current photo (or an empty state) with clear Replace / Undo actions.
 * Replaces a dashed button labeled with the raw storage filename.
 */
export const ProductPhotoField = ({
  file,
  imageUrl,
  onFileChange,
}: ProductPhotoFieldProps) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const accept = (picked: File | undefined | null) => {
    if (!picked) return;
    if (!ACCEPTED.includes(picked.type)) {
      toast.error('Please choose a JPG, PNG, GIF or WebP image.');
      return;
    }
    if (picked.size > MAX_BYTES) {
      toast.error('That image is over 10 MB — please choose a smaller one.');
      return;
    }
    onFileChange(picked);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    accept(e.dataTransfer.files?.[0]);
  };

  const hasImage = !!imageUrl;
  const accent = theme.palette.primary.main;

  return (
    <CommonFormControl label="Image:" sx={{ mt: 1 }}>
      <Box sx={{ width: '100%' }}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(',')}
          hidden
          onChange={(e) => {
            accept(e.target.files?.[0]);
            e.target.value = ''; // allow re-picking the same file
          }}
        />
        <Box
          role="button"
          tabIndex={0}
          aria-label={
            hasImage ? 'Replace property photo' : 'Upload property photo'
          }
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            border: '2px dashed',
            borderColor: dragging ? accent : theme.palette.divider,
            bgcolor: dragging
              ? alpha(accent, 0.06)
              : alpha(theme.palette.text.primary, 0.02),
            cursor: 'pointer',
            transition: 'border-color 120ms, background-color 120ms',
            '&:hover, &:focus-visible': {
              borderColor: accent,
              bgcolor: alpha(accent, 0.04),
              outline: 'none',
            },
            '&:hover .photo-overlay, &:focus-visible .photo-overlay': {
              opacity: 1,
            },
          }}
        >
          {hasImage ? (
            <>
              <Box
                component="img"
                src={imageUrl}
                alt="Property photo"
                sx={{
                  display: 'block',
                  width: '100%',
                  height: 220,
                  objectFit: 'cover',
                }}
              />
              <Box
                className="photo-overlay"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.common.black, 0.45),
                  color: theme.palette.common.white,
                  opacity: dragging ? 1 : 0,
                  transition: 'opacity 150ms',
                  gap: 1,
                }}
              >
                <i
                  className="fa-light fa-camera"
                  style={{ fontSize: '1.25rem' }}
                />
                <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                  {dragging ? 'Drop to replace' : 'Click or drop to replace'}
                </Typography>
              </Box>
            </>
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ py: 5, px: 2, textAlign: 'center' }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(accent, 0.1),
                  color: accent,
                }}
              >
                <i
                  className="fa-light fa-cloud-arrow-up"
                  style={{ fontSize: '1.4rem' }}
                />
              </Box>
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                {dragging ? 'Drop your photo here' : 'Upload a property photo'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag and drop, or{' '}
                <Box component="span" sx={{ color: accent, fontWeight: 600 }}>
                  browse
                </Box>
              </Typography>
            </Stack>
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mt: 1 }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ minWidth: 0 }}
          >
            {file
              ? `New photo: ${file.name} (${formatSize(
                  file.size
                )}) — saved when you click Save`
              : 'JPG, PNG, GIF or WebP · up to 10 MB'}
          </Typography>
          <Stack direction="row" spacing={1}>
            {hasImage && (
              <Button
                size="small"
                variant="outlined"
                onClick={openPicker}
                startIcon={<i className="fa-light fa-arrow-up-from-bracket" />}
              >
                Replace photo
              </Button>
            )}
            {file && (
              <Button
                size="small"
                color="inherit"
                onClick={() => onFileChange(null)}
              >
                Undo change
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </CommonFormControl>
  );
};
